import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Compass,
  X,
  ChevronRight,
  ChevronLeft,
  Sparkles,
  Command,
  ArrowRight,
  HelpCircle,
  Lightbulb,
  CheckCircle2,
} from 'lucide-react';
import { useGuideTour } from '@/context/GuideTourContext';
import { Button } from './Button';

interface SpotlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export function GuideTourModal() {
  const {
    isTourActive,
    currentStepIndex,
    currentStep,
    totalSteps,
    stopTour,
    nextStep,
    prevStep,
    goToStep,
  } = useGuideTour();

  const [targetRect, setTargetRect] = useState<SpotlightRect | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  // Position spotlight on active element
  useEffect(() => {
    if (!isTourActive) {
      setTargetRect(null);
      return;
    }

    const updatePosition = () => {
      if (!currentStep.targetSelector) {
        setTargetRect(null);
        return;
      }

      const el = document.querySelector(currentStep.targetSelector);
      if (el) {
        const rect = el.getBoundingClientRect();
        // Add pleasant padding around the target
        const padding = 8;
        setTargetRect({
          top: Math.max(0, rect.top - padding),
          left: Math.max(0, rect.left - padding),
          width: rect.width + padding * 2,
          height: rect.height + padding * 2,
        });

        // Ensure the element is visible in the viewport
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      } else {
        setTargetRect(null);
      }
    };

    // Small delay to allow any router navigation or DOM rendering
    const timer = setTimeout(updatePosition, 120);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isTourActive, currentStep]);

  if (!isTourActive) return null;

  const isFirst = currentStepIndex === 0;
  const isLast = currentStepIndex === totalSteps - 1;
  const progressPercent = Math.round(((currentStepIndex + 1) / totalSteps) * 100);

  return (
    <div className="fixed inset-0 z-50 overflow-hidden pointer-events-auto">
      {/* Dynamic SVG Spotlight Cutout */}
      <svg
        className="fixed inset-0 w-full h-full pointer-events-none transition-all duration-300"
        style={{ zIndex: 51 }}
      >
        <defs>
          <mask id="tour-spotlight-mask">
            {/* White covers entire screen (opaque backdrop) */}
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {/* Black cuts out the spotlight hole */}
            {targetRect && (
              <rect
                x={targetRect.left}
                y={targetRect.top}
                width={targetRect.width}
                height={targetRect.height}
                rx="14"
                ry="14"
                fill="black"
              />
            )}
          </mask>
        </defs>
        {/* Dark translucent backdrop with the cutout mask */}
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(8, 10, 18, 0.78)"
          mask="url(#tour-spotlight-mask)"
        />
      </svg>

      {/* Target glowing halo */}
      {targetRect && (
        <motion.div
          layoutId="tour-highlight-box"
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{
            opacity: 1,
            scale: 1,
            top: targetRect.top,
            left: targetRect.left,
            width: targetRect.width,
            height: targetRect.height,
          }}
          transition={{ type: 'spring', damping: 26, stiffness: 320 }}
          className="fixed pointer-events-none rounded-2xl border-2 border-primary-400/90 shadow-[0_0_30px_rgba(139,92,246,0.45)] ring-4 ring-primary-500/20"
          style={{ zIndex: 52 }}
        />
      )}

      {/* Tour Card Container */}
      <div className="fixed inset-0 z-[53] pointer-events-none flex items-end sm:items-center justify-center p-4 sm:p-6">
        <motion.div
          ref={cardRef}
          initial={{ opacity: 0, y: 24, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.95 }}
          transition={{ duration: 0.25, ease: 'easeOut' }}
          className="pointer-events-auto w-full max-w-lg rounded-3xl border border-primary-500/30 bg-khiimori-bg-elevated/95 p-6 shadow-2xl backdrop-blur-2xl text-khiimori-text-primary ring-1 ring-white/10"
          style={{
            background:
              'radial-gradient(circle at 85% 15%, var(--khiimori-glow-violet), transparent 60%), linear-gradient(180deg, var(--khiimori-bg-secondary), var(--khiimori-bg-elevated))',
          }}
        >
          {/* Top Bar: Badge & Progress & Close */}
          <div className="flex items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-primary-500/20 text-primary-300 border border-primary-500/30">
                <Compass className="w-3.5 h-3.5 text-primary-400 animate-spin-slow" />
                {currentStep.badge || `Step ${currentStepIndex + 1} of ${totalSteps}`}
              </span>
              <span className="text-xs text-khiimori-text-secondary/50 font-medium">
                {currentStepIndex + 1} / {totalSteps}
              </span>
            </div>

            <button
              onClick={stopTour}
              aria-label="Exit Guide Tour"
              className="p-1.5 rounded-xl text-khiimori-text-secondary/50 hover:text-khiimori-text-primary hover:bg-khiimori-bg-tertiary/60 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Progress Bar */}
          <div className="w-full bg-khiimori-bg-tertiary/60 h-1.5 rounded-full overflow-hidden mb-5">
            <motion.div
              className="h-full bg-gradient-to-r from-primary-500 via-primary-400 to-khiimori-accent-fuchsia"
              initial={{ width: 0 }}
              animate={{ width: `${progressPercent}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>

          {/* Step Body */}
          <div className="space-y-3 mb-6">
            <h3 className="text-xl font-bold tracking-tight text-khiimori-text-primary flex items-center gap-2">
              {currentStep.title}
            </h3>
            <p className="text-sm leading-relaxed text-khiimori-text-secondary/80">
              {currentStep.description}
            </p>

            {currentStep.actionHint && (
              <div className="flex items-start gap-2.5 p-3 rounded-xl bg-primary-500/10 border border-primary-500/20 text-xs text-primary-200">
                <Lightbulb className="w-4 h-4 text-primary-300 shrink-0 mt-0.5" />
                <span>{currentStep.actionHint}</span>
              </div>
            )}
          </div>

          {/* Step Dots indicator */}
          <div className="flex items-center justify-between pt-2 border-t border-khiimori-border/40">
            <div className="flex items-center gap-1.5">
              {Array.from({ length: totalSteps }).map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => goToStep(idx)}
                  className={`h-2 rounded-full transition-all duration-200 ${
                    idx === currentStepIndex
                      ? 'w-6 bg-primary-400'
                      : idx < currentStepIndex
                      ? 'w-2 bg-primary-500/40 hover:bg-primary-500/60'
                      : 'w-2 bg-khiimori-bg-tertiary hover:bg-khiimori-text-secondary/30'
                  }`}
                  aria-label={`Go to step ${idx + 1}`}
                />
              ))}
            </div>

            {/* Navigation Buttons */}
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={prevStep}
                disabled={isFirst}
                className="text-xs px-3"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </Button>

              <Button
                variant="gradient"
                size="sm"
                onClick={nextStep}
                className="text-xs px-4 font-semibold shadow-md shadow-primary-500/20"
              >
                {isLast ? (
                  <>
                    Finish Tour
                    <CheckCircle2 className="w-4 h-4 ml-1.5 text-success-300" />
                  </>
                ) : (
                  <>
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
